<script lang="ts">
import "@cisco-u/icons/icons/cu-icon-cart-thick.js";
import "@cisco-u/text-badge/cu-text-badge.js";
import "@cisco-u/notification/cu-notification.js";

import { defineComponent } from "vue";
import { mapGetters } from "vuex";

import AiPromptBtn from "@/components/AiPromptBtn.vue";
import CalloutSearchItem from "@/components/CalloutSearchItem.vue";
import { SERVICEDESK_PORTAL_URL } from "@/consts/externalUrls";
import ProfileIcon from "@/layout/Header/dropDown.vue";
import nav from "@/layout/Header/nav.json";
import navAi from "@/layout/Header/nav-ai.json";
import { EventBus } from "@/store/eventBus";
import { hasGenAIAccess } from "@/utils/genAIConsent";
import SanitizeUrl from "@/utils/sanitizeUrl";
import telemetry from "@/utils/telemetry";

const searchAll = "search-all";
const searchType = "search-type";

export default defineComponent({
  components: {
    ProfileIcon,
    AiPromptBtn,
    CalloutSearchItem,
  },
  emits: ["update-query"],
  props: {
    dark: {
      type: Boolean,
      default: false,
    },
    scrolled: {
      type: Boolean,
      default: false,
    },
    showLogo: {
      type: Boolean,
      default: false,
    },
    instance: {
      type: String,
      default: "",
    },
    searchQuery: {
      type: String,
      default: "",
    },
    isScrolled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      cartItems: 0,
      query: this.searchQuery,
      errorMounted: "",
      showDropDown: false,
      lastQuery: "",
      SERVICEDESK_PORTAL_URL,
    };
  },
  mounted() {
    this.queryParamSetup();
    // Retrieve cart quantity from local storage
    const savedCartItems = localStorage.getItem("cartItems");
    if (savedCartItems) {
      this.cartItems = Number.parseInt(savedCartItems, 10);
    }

    // Remove any existing listeners before adding a new one
    EventBus.off("updateQty");
    EventBus.on("updateQty", (qty: number) => {
      this.cartItems = qty;
      // Save cart quantity to local storage
      localStorage.setItem("cartItems", qty.toString());
    });
    EventBus.on("updateHeaderDropDown", this.onUpdateHeaderDropDown);
    EventBus.on("pathSearchReset", this.onPathSearchReset);
  },
  beforeUnmount() {
    EventBus.off("pathSearchReset", this.onPathSearchReset);
    EventBus.off("updateHeaderDropDown", this.onUpdateHeaderDropDown);
  },
  computed: {
    ...mapGetters({
      isAdminRole: "user/adminRole",
      isCustomerAdminRole: "user/customerAdminRole",
      userStatus: "user/status",
    }),
    customerAdminUrl(): string {
      return SanitizeUrl(globalThis.env.CUSTOMER_ADMIN_URL);
    },
    adminUrl(): string {
      return SanitizeUrl(globalThis.env.CISCO_ADMIN_URL);
    },
    isSearchRoute(): boolean {
      return (
        this.$route &&
        [searchAll, searchType].includes(this.$route.name as string)
      );
    },
    showGenAi(): boolean {
      return hasGenAIAccess(this.userStatus);
    },
    showProfile() {
      if (this.$route?.meta?.scrollShowProfile) {
        return true;
      }
      return this.scrolled;
    },
    showNav() {
      return this.$route?.meta?.hideMarquee;
    },
    noRounding(): boolean {
      return (
        (this.$route?.meta?.pageHeader as boolean) &&
        (this.$route?.meta?.hideMarquee as boolean)
      );
    },
    isExploreSubPages() {
      return ["/explore/", "/podcast/"].includes(this.$route.path);
    },
    navigationList() {
      let navigation = [];
      if (this.showGenAi && (this.showLogo || this.showNav)) {
        navigation = navAi;
      } else {
        navigation = nav;
      }

      navigation = navigation.filter((navItem) => navItem.title !== "Plans");
      // nav, but remove the last item
      return navigation.slice(0, -1);
    },
    searchInPath() {
      return globalThis.env.ENABLE_SEARCH_IN_PATH;
    },
    isPathPage(): boolean {
      return this.$route && this.$route.name == "Path";
    },
  },
  watch: {
    $route() {
      this.queryParamSetup();
    },
    searchQuery(newQuery) {
      this.query = newQuery;
    },
  },
  methods: {
    goToCart() {
      if (globalThis.location.pathname == "/purchase") {
        EventBus.emit("showCart");
      } else {
        this.$router.push({ name: "purchase" });
      }
    },
    updateQuery(newValue: any) {
      this.$emit("update-query", newValue);
    },
    queryParamSetup() {
      if (this.isSearchRoute && this.$route.query && this.$route.query.query) {
        this.query = this.$route.query.query as string;
        if (this.errorMounted !== "") {
          telemetry.custom404("learner-navPath", this.errorMounted, "Search");
        }
      }
    },
    startSearching(e: any) {
      const { search, type } = e?.detail ?? {};
      const query = typeof search === "string" ? search : "";

      this.query = query;

      if (query.length < 2) return;

      if (this.isPathPage && type === "lp") {
        if (query === this.lastQuery) return;
        this.lastQuery = query;
        EventBus.emit("updatePathSearch", {
          query,
          referrerUrl: globalThis.location.href,
        });
        return;
      }

      this.redirectToSearch();
    },
    redirectToSearch() {
      // Get current query parameters and clean out any existing referrerUrl
      const routeQuery = this.$route.query || {};
      const { referrerUrl, ...currentQuery } = routeQuery;
      currentQuery.query = this.query;

      // Create clean referrer URL without existing referrerUrl parameter
      const currentUrl = new URL(globalThis.location.href);
      currentUrl.searchParams.delete("referrerUrl");
      currentQuery.referrerUrl = currentUrl.toString();

      this.$router.push({ name: searchAll, query: currentQuery });
    },
    clearStorage({ detail }: CustomEvent) {
      detail?.event.preventDefault();
      localStorage.clear();
      globalThis.location.href = "/sso-logout";
    },
    onUpdateHeaderDropDown(showPremium: boolean) {
      this.showDropDown = !showPremium;
    },
    onPathSearchReset() {
      this.lastQuery = "";
    },
  },
});
</script>

<template>
  <div
    :data-cy="`page-header-${instance}`"
    :class="[
      'z-[1000]',
      {
        'rounded bg-black-dark': dark,
        'bg-white-lightest pt-0 dark:bg-black-lightest': !dark,
        'left-0 right-0 top-0 fixed': scrolled || noRounding,
        'rounded-t-40': !scrolled && !noRounding,
      },
    ]"
  >
    <callout-search-item v-if="dark || showLogo" class="lg:text-22" />

    <cu-top-header
      @cu-header-search="startSearching"
      :searchtext="query"
      logolink="/"
      :dark="dark"
      v-if="showLogo || showNav"
      :learnersearch="searchInPath && isPathPage && showDropDown"
      data-cy="page-header"
    >
      <router-link
        v-for="navitem in navigationList"
        :key="navitem.title"
        :data-cy="!dark ? navitem.navCy : `${navitem.navCy}-dark`"
        active-class="active"
        :to="{ name: navitem.route }"
        slot="nav-item"
        >{{ navitem.title }}</router-link
      >

      <ai-prompt-btn
        v-if="showGenAi && (showLogo || showNav)"
        slot="action-item"
        class="p-[0.5625rem]"
        :isScrolled="isScrolled"
        :dark="dark"
      />
      <profile-icon
        v-if="showLogo || showNav"
        :scrolled="showProfile"
        :show-pic="showProfile"
        :dark="dark"
        profile-dropdown="toggle-dropdown"
        slot="action-item"
      />
      <cu-notification
        slot="action-item"
        data-cy="user-cart"
        size="m"
        type="cart"
        @click="goToCart"
        :items="cartItems"
        :dark="dark"
        >Go to Cart</cu-notification
      >

      <cu-top-mobile-section slot="mobile-nav-item">
        <router-link
          v-for="navitem in navigationList"
          :key="navitem.title"
          :to="{ name: navitem.route }"
          v-slot="{ href }"
          custom
        >
          <cu-top-mobile-item
            :href="href"
            :active="navitem.route === $route.name"
            :data-cy="
              !dark ? `${navitem.navCy}-mobile` : `${navitem.navCy}-mobile-dark`
            "
            >{{ navitem.title }}</cu-top-mobile-item
          >
        </router-link>
      </cu-top-mobile-section>

      <cu-top-mobile-section slot="mobile-nav-item">
        <router-link :to="{ name: 'dashboard' }" v-slot="{ href }" custom>
          <cu-top-mobile-item data-cy="my-dashboard" :href="href"
            >My Dashboard</cu-top-mobile-item
          >
        </router-link>
        <cu-top-mobile-item
          v-if="isCustomerAdminRole"
          data-cy="customer-admin-mobile"
          :href="customerAdminUrl"
          rel="noopener"
          >Customer Admin</cu-top-mobile-item
        >
        <cu-top-mobile-item
          v-if="isAdminRole"
          data-cy="admin-mobile"
          :href="adminUrl"
          target="_blank"
          rel="noopener"
          >Cisco Admin</cu-top-mobile-item
        >
      </cu-top-mobile-section>

      <cu-top-mobile-item
        :href="SERVICEDESK_PORTAL_URL"
        target="_blank"
        rel="noopener"
        data-cy="help-support"
        slot="mobile-footer-item"
        >Help &amp; Support</cu-top-mobile-item
      >
      <cu-top-mobile-item
        @cu-mobile-item="clearStorage"
        href="/sso-logout"
        slot="mobile-footer-item"
        data-cy="logout"
        >Sign Out</cu-top-mobile-item
      >
    </cu-top-header>
  </div>
</template>
